import { useEffect, useRef, useState } from 'react';
import { normalizeCustomerReference, setCustomerReference } from '../../models/queries/queriesV1Model';

export const canEditCustomerReference = (allowed, status) => allowed && !['draft', 'cancelled', 'completed', 'disabled'].includes(String(status || '').toLowerCase());
export function useCustomerReference({ queryPk, value, status, allowed, onSaved }) {
  const [current, setCurrent] = useState(value ?? null), [text, setText] = useState(value || ''), [error, setError] = useState(''), [terminal, setTerminal] = useState(false), [busy, setBusy] = useState(false);
  const generation = useRef(0), pending = useRef(false);
  useEffect(() => { generation.current++; pending.current = false; setCurrent(value ?? null); setText(value || ''); setError(''); setTerminal(false); setBusy(false); }, [queryPk, value]);
  useEffect(() => () => { generation.current++; }, []);
  const editable = canEditCustomerReference(allowed, status) && !terminal;
  const save = async () => {
    if (!editable || pending.current) return false;
    const scope = generation.current;
    pending.current = true; setBusy(true); setError('');
    try {
      const normalized = normalizeCustomerReference(text);
      const result = await setCustomerReference(queryPk, normalized, current);
      if (scope !== generation.current) return false;
      setCurrent(result.customer_reference ?? null); setText(result.customer_reference || ''); onSaved?.(result);
      return true;
    } catch (e) {
      if (scope !== generation.current) return false;
      if (e.code === 'CUSTOMER_REFERENCE_CHANGED') {
        setCurrent(e.data?.customer_reference ?? null);
        setError('Someone else changed it to ' + (e.data?.customer_reference || '(empty)') + '. Check your value and save again.');
      } else if (e.code === 'QUERY_TERMINAL') {
        setTerminal(true); setError('This query is closed; its customer reference cannot be changed.');
      } else setError(e.message + (e.requestId ? ' (Request: ' + e.requestId + ')' : ''));
      return false;
    } finally { if (scope === generation.current) { pending.current = false; setBusy(false); } }
  };
  return { current, text, setText, error, busy, editable, save };
}
